import React, { useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, OrgCreditsOverview, OrgLedgerRow, OrgTerms, RateCardRow } from '../lib/api';
import { Coins, Loader2, RefreshCw, Save } from 'lucide-react';
import { Banner, GrantCreditsForm, LeadBatchForm, OrgRateCardTable, reasonOf } from './CreditsKit';

/**
 * Credits & terms — one organisation's money-free billing (28 Sep, mig 0702).
 *
 * The owner: "Everything is done as a hybrid model of you pay X number per
 * lead, and then a success fee that is agreed on based on your use case."
 * So this card holds, for ONE organisation:
 *   · credits left, the runway, and what lapses within 30 days;
 *   · the TERMS: what the fee was agreed for (use case), the per-lead price
 *     and the three success fees — each of those is this org's own number on
 *     the rate card (lead_loaded / outcome_*), so there is one source;
 *   · the rest of its rate card, editable;
 *   · adding credits (one org-wide lot) and adding credits for a lead batch;
 *   · the latest ledger rows, including the 28 Sep "balances merged" row.
 * The USD-per-credit rate and "flows into plan and pack pricing" are gone:
 * no money is shown to anyone, and there are no plans or packs.
 */

const SUCCESS: { key: 'qualified' | 'meeting_booked' | 'converted'; action: string; label: string }[] = [
  { key: 'qualified', action: 'outcome_qualified', label: 'Lead qualified' },
  { key: 'meeting_booked', action: 'outcome_meeting_booked', label: 'Meeting booked' },
  { key: 'converted', action: 'outcome_converted', label: 'Lead converted' },
];

const SOURCE_LABEL: Record<string, string> = {
  subscription: 'Earlier plan', pack: 'Earlier top-up', admin_grant: 'Added by SuprAgent',
  trial: 'Free trial', lead_preload: 'Lead batch', refund: 'Refund',
};

const day = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export const OrgBillingCard: React.FC<{ orgId: string; onChanged?: () => void }> = ({ orgId, onChanged }) => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [ov, setOv] = useState<OrgCreditsOverview | null>(null);
  const [card, setCard] = useState<RateCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<'grant' | 'batch' | null>(null);
  const [ledgerKey, setLedgerKey] = useState(0);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [o, c] = await Promise.all([api.getOrgCreditsOverview(orgId), api.getEffectiveRateCard(orgId)]);
      setOv(o);
      setCard(c);
    } catch (e) {
      setLoadError(reasonOf(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orgId]);

  const afterGrant = (msg: string) => {
    setNotice(msg); setOpen(null); setLedgerKey(k => k + 1);
    load(); onChanged?.();
  };

  if (loading && !ov) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        Loading credits…
      </div>
    );
  }

  const left = ov ? (ov.credits_left ?? ov.balance) : null;
  const runway = ov ? (ov.runway_days !== undefined ? ov.runway_days : ov.burn?.runway_days) : null;
  const exp = ov?.expiring_30d;
  const expiring = typeof exp === 'number' ? { credits: exp, first_expiry: null as string | null } : exp;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Credits &amp; terms</h3>
        </div>
        <button onClick={() => { load(); setLedgerKey(k => k + 1); }} className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="p-6 space-y-6">
        {loadError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm flex items-start justify-between gap-4">
            <div>
              <div>Couldn&apos;t load this organisation&apos;s credits. Retry before changing anything below.</div>
              <div className="text-xs text-red-600 mt-1">{loadError}</div>
            </div>
            <button onClick={load} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-red-300 text-red-700 rounded hover:bg-red-100 shrink-0">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        )}

        {/* The balance */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-500">Credits left</p>
            <p className={`text-2xl font-semibold tabular-nums ${left != null && left <= 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {left == null ? '—' : left.toLocaleString()}
            </p>
            {left != null && left <= 0 && <p className="text-xs text-red-600">Out of credits: its agents are paused.</p>}
          </div>
          <div>
            <p className="text-xs text-gray-500">Runway</p>
            <p className="text-lg font-semibold text-gray-900">
              {runway == null ? 'No spend in 30 days' : `About ${Math.floor(runway).toLocaleString()} days`}
            </p>
            {ov?.burn && <p className="text-xs text-gray-500">{ov.burn.last_30d.toLocaleString()} spent in the last 30 days</p>}
          </div>
          <div>
            <p className="text-xs text-gray-500">Expiring within 30 days</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums">{(expiring?.credits ?? 0).toLocaleString()}</p>
            {expiring?.first_expiry && <p className="text-xs text-gray-500">First on {day(expiring.first_expiry)}</p>}
          </div>
        </div>

        {/* Add credits */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => setOpen(open === 'grant' ? null : 'grant')} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              {open === 'grant' ? 'Cancel' : 'Add credits'}
            </button>
            <button onClick={() => setOpen(open === 'batch' ? null : 'batch')} className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">
              {open === 'batch' ? 'Cancel' : 'Add credits for a lead batch'}
            </button>
          </div>
          {/* 28 Sep: grant_credits turns a trial org 'subscribed' (credits added
              by SuprAgent mean terms were agreed). Say it before the click. */}
          {(ov?.billing_state === 'trial' || ov?.billing_state === 'trial_expired') && (
            <p className="mt-2 text-xs text-amber-700">
              This organisation is on a free trial{ov.billing_state === 'trial_expired' ? ' that has ended' : ''}. Adding credits ends the trial and makes it active.
            </p>
          )}
          {open === 'grant' && <div className="mt-3"><GrantCreditsForm api={api} orgId={orgId} onDone={afterGrant} /></div>}
          {open === 'batch' && <div className="mt-3"><LeadBatchForm api={api} orgId={orgId} onDone={afterGrant} /></div>}
          {notice && <div className="mt-3"><Banner kind="success">{notice}</Banner></div>}
        </div>

        {/* Terms */}
        {ov && (
          <TermsEditor
            api={api}
            orgId={orgId}
            terms={ov.terms}
            card={card}
            onSaved={(t) => { setOv({ ...ov, terms: t }); api.getEffectiveRateCard(orgId).then(setCard).catch(() => {}); }}
          />
        )}

        {/* The rest of its card */}
        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-900">Everything else on its card</h4>
          <p className="text-xs text-gray-500 mb-2">Type a number to give this organisation its own price; clear it to go back to the default.</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <OrgRateCardTable api={api} orgId={orgId} rows={card} onChange={setCard} hideTermsRows />
          </div>
        </div>

        {/* Ledger */}
        <LedgerList key={ledgerKey} api={api} orgId={orgId} lots={ov?.lots ?? []} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Terms: use case, per lead, success fees, notes
// ---------------------------------------------------------------------------

const TermsEditor: React.FC<{
  api: AdminAPI;
  orgId: string;
  terms: OrgTerms;
  card: RateCardRow[];
  onSaved: (t: OrgTerms) => void;
}> = ({ api, orgId, terms, card, onSaved }) => {
  const byAction = (a: string) => card.find(r => r.action === a);
  const agreed = (a: string) => byAction(a)?.resolved_from === 'org_override';
  const initial = () => ({
    useCase: terms.use_case ?? '',
    notes: terms.notes ?? '',
    perLead: agreed('lead_loaded') || terms.per_lead_agreed ? String(terms.per_lead ?? byAction('lead_loaded')?.credits ?? '') : '',
    fees: Object.fromEntries(SUCCESS.map(s => [
      s.key,
      agreed(s.action) || terms.success_fees_agreed?.[s.key] ? String(terms.success_fees?.[s.key] ?? byAction(s.action)?.credits ?? '') : '',
    ])) as Record<string, string>,
  });
  const [f, setFState] = useState(initial);
  // 28 Sep: pricing a row in the table below changes `card`, which used to
  // refill this form and throw away unsaved typing. Refill only while clean.
  const [dirty, setDirty] = useState(false);
  const setF = (next: typeof f) => { setDirty(true); setFState(next); };
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; isError: boolean } | null>(null);
  useEffect(() => { if (!dirty) setFState(initial()); }, [terms, card]);

  const intOrNull = (v: string) => (v.trim() === '' ? null : Number(v));
  const bad = [f.perLead, ...Object.values(f.fees)].some(v => v.trim() !== '' && !(Number.isInteger(Number(v)) && Number(v) >= 0));

  const save = async () => {
    if (bad) { setMsg({ text: 'Credits must be whole numbers, 0 or more.', isError: true }); return; }
    setBusy(true); setMsg(null);
    try {
      const perLead = intOrNull(f.perLead);
      const body: Parameters<AdminAPI['setOrgTerms']>[1] = {
        // 28 Sep: an empty string CLEARS the field (the API keeps a null as
        // "leave it"), so a blanked use case or note really goes away.
        use_case: f.useCase.trim(),
        notes: f.notes.trim(),
        // A blank success fee clears this org's number (back to the default).
        success_fees: Object.fromEntries(SUCCESS.map(s => [s.key, intOrNull(f.fees[s.key])])),
      };
      if (perLead !== null) body.per_lead = perLead;
      let t = await api.setOrgTerms(orgId, body);
      // A blank per-lead price clears the org's lead_loaded number too.
      if (perLead === null && agreed('lead_loaded')) {
        await api.clearOrgOverride(orgId, 'lead_loaded');
        t = await api.getOrgTerms(orgId);
      }
      setDirty(false);
      onSaved(t);
      setMsg({ text: 'Terms saved', isError: false });
    } catch (e) {
      setMsg({ text: `Couldn't save the terms: ${reasonOf(e)}`, isError: true });
    } finally { setBusy(false); }
  };

  const numInput = (value: string, onChange: (v: string) => void, placeholder: string, label: string) => (
    <input
      type="number" min={0} step={1} value={value} placeholder={placeholder} aria-label={label}
      onChange={e => onChange(e.target.value)}
      className="mt-1 block w-28 rounded border-gray-300 text-sm tabular-nums"
    />
  );

  return (
    <div className="border-t border-gray-100 pt-4 space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Terms</h4>
        <p className="text-xs text-gray-500">
          What this organisation pays: credits per lead, and a success fee agreed for its use case. Leave a number blank to use the default card.
        </p>
      </div>
      <label className="block text-xs text-gray-600">Use case (what the fee was agreed for)
        <input type="text" value={f.useCase} onChange={e => setF({ ...f, useCase: e.target.value })}
          className="mt-1 block w-full max-w-lg rounded border-gray-300 text-sm" placeholder="e.g. motor insurance renewals" />
      </label>
      <div className="flex flex-wrap gap-6">
        <label className="block text-xs text-gray-600">Per lead (credits)
          {numInput(f.perLead, v => setF({ ...f, perLead: v }), String(byAction('lead_loaded')?.default_credits ?? 0), 'Per lead credits')}
        </label>
        <fieldset className="flex flex-wrap gap-4">
          <legend className="text-xs text-gray-600 mb-0.5">Success fees (credits)</legend>
          {SUCCESS.map(s => (
            <label key={s.key} className="block text-xs text-gray-500">{s.label}
              {numInput(
                f.fees[s.key] ?? '',
                v => setF({ ...f, fees: { ...f.fees, [s.key]: v } }),
                String(byAction(s.action)?.default_credits ?? 0),
                `Success fee: ${s.label}`,
              )}
            </label>
          ))}
        </fieldset>
      </div>
      {/* 28 Sep: this note is printed under "How you pay" on the customer's
          Credits page, so say so — internal remarks must not land there. */}
      <label className="block text-xs text-gray-600">Note for the customer (shown on their Credits page)
        <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2}
          className="mt-1 block w-full max-w-lg rounded border-gray-300 text-sm" />
      </label>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy}
          className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded text-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save terms
        </button>
        {msg && <span className={`text-xs ${msg.isError ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>}
        {terms.updated_at && <span className="text-xs text-gray-400">Last changed {day(terms.updated_at)}{terms.updated_by ? ` by ${terms.updated_by}` : ''}</span>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// The latest ledger rows
// ---------------------------------------------------------------------------

const LedgerList: React.FC<{ api: AdminAPI; orgId: string; lots: OrgCreditsOverview['lots'] }> = ({ api, orgId, lots }) => {
  const [rows, setRows] = useState<OrgLedgerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.getOrgLedger(orgId, 20).then(setRows).catch(e => setError(reasonOf(e)));
  }, [orgId]);

  const label = (r: OrgLedgerRow) => {
    const kind = r.metadata?.kind;
    if (kind === 'pools_merged_0702') return 'Balances merged into one (28 Sep)';
    if (kind === 'credit_request') return 'Asked for more credits';
    // 28 Sep: grants are written as action 'admin_grant' with the real source in
    // metadata.source (trial approvals, the signup trial lot, lead batches), so
    // read that before the action or every trial reads "Added by SuprAgent".
    if (r.credits < 0) return SOURCE_LABEL[kind] || SOURCE_LABEL[r.metadata?.source] || SOURCE_LABEL[r.action] || 'Credits added';
    return r.label || r.action.replace(/_/g, ' ');
  };
  const detail = (r: OrgLedgerRow): string | null => {
    const m = r.metadata || {};
    if (m.kind === 'pools_merged_0702') {
      const before = m.before?.total_credits_balance;
      const after = m.after_balance;
      if (before != null && after != null) return `${Number(before).toLocaleString()} → ${Number(after).toLocaleString()}`;
    }
    if (m.note) return String(m.note);
    return null;
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Latest ledger</h4>
      {error ? (
        // If the ledger fails to load, the lots still say where credits came from.
        <div className="space-y-2">
          <p className="text-xs text-gray-500">The ledger couldn&apos;t be loaded ({error}). Credit lots:</p>
          <ul className="text-xs text-gray-600 space-y-1">
            {lots.slice(0, 20).map(l => (
              <li key={l.id} className="flex justify-between gap-4">
                <span>{SOURCE_LABEL[l.source] || 'Credits added'} · {day(l.granted_at)}{l.status !== 'active' ? ` · ${l.status}` : ''}</span>
                <span className="tabular-nums">{l.remaining.toLocaleString()} of {l.granted.toLocaleString()} left</span>
              </li>
            ))}
            {lots.length === 0 && <li className="text-gray-400">No credit lots.</li>}
          </ul>
        </div>
      ) : rows === null ? (
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">Nothing in the ledger yet.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400">
              <th className="font-medium pb-1">When</th>
              <th className="font-medium pb-1">What</th>
              <th className="font-medium pb-1 text-right">Credits</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.id}>
                <td className="py-1.5 pr-3 text-xs text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-1.5 pr-3 text-gray-900">
                  {label(r)}
                  {detail(r) && <span className="ml-2 text-xs text-gray-500">{detail(r)}</span>}
                  {r.agent_ref && <span className="ml-2 text-xs text-gray-400 font-mono">{r.agent_ref}</span>}
                </td>
                <td className={`py-1.5 text-right tabular-nums font-medium ${r.credits < 0 ? 'text-emerald-700' : r.credits === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                  {r.credits < 0 ? `+${(-r.credits).toLocaleString()}` : r.credits === 0 ? '0' : `−${r.credits.toLocaleString()}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OrgBillingCard;
